import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { LegalSection } from "@/components/legal/LegalSection";

const tableOfContents = [
  { id: "introduction", title: "Introduction", level: 1 },
  { id: "information-we-collect", title: "Information We Collect", level: 1 },
  {
    id: "how-we-use-information",
    title: "How We Use Your Information",
    level: 1,
  },
  {
    id: "emergency-location-services",
    title: "Emergency Services and Location Data",
    level: 1,
  },
  {
    id: "information-sharing",
    title: "Information Sharing and Disclosure",
    level: 1,
  },
  { id: "data-security", title: "Data Security", level: 1 },
  { id: "your-rights", title: "Your Rights and Choices", level: 1 },
  { id: "cookies", title: "Cookies and Tracking Technologies", level: 1 },
  {
    id: "international-transfers",
    title: "International Data Transfers",
    level: 1,
  },
  { id: "children-privacy", title: "Children's Privacy", level: 1 },
  { id: "changes", title: "Changes to This Policy", level: 1 },
  { id: "contact", title: "Contact Us", level: 1 },
];

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="January 6, 2025"
      language="en"
      alternateUrl="/pages/vi/privacy"
      tableOfContents={tableOfContents}
    >
      <LegalSection id="introduction" title="Introduction">
        <p>
          Welcome to Locuno, a social networking platform that connects people
          within local communities. At Locuno, we are committed to protecting
          your privacy and ensuring transparency about how we collect, use, and
          protect your personal information.
        </p>
        <p>
          This Privacy Policy explains how Cloudjet Solutions Pte. Ltd.
          ("Locuno," "we," "us," or "our") collects, uses, shares, and protects
          information about you when you use our social networking services,
          including our website, mobile applications, and any other services we
          offer (collectively, the "Services").
        </p>
        <p>
          By using our Services, you agree to the collection and use of
          information in accordance with this policy. If you do not agree with
          our policies and practices, please do not use our Services.
        </p>
      </LegalSection>

      <LegalSection id="information-we-collect" title="Information We Collect">
        <h3>Information You Provide to Us</h3>
        <p>We collect information you provide directly to us, including:</p>
        <ul>
          <li>
            <strong>Account Information:</strong> Username, email address,
            password, profile picture, and display name
          </li>
          <li>
            <strong>Profile Information:</strong> Biographical information,
            interests, location, and other profile details you choose to share
          </li>
          <li>
            <strong>Content:</strong> Posts, comments, messages, photos, videos,
            and other content you create or share
          </li>
          <li>
            <strong>Communications:</strong> Messages you send to us or other
            users through our platform
          </li>
          <li>
            <strong>Location Data:</strong> Your location when you choose to
            share it or enable location services
          </li>
        </ul>

        <h3>Information We Collect Automatically</h3>
        <p>
          When you use our Services, we automatically collect certain
          information:
        </p>
        <ul>
          <li>
            <strong>Usage Information:</strong> How you interact with our
            Services, including pages visited, features used, and time spent
          </li>
          <li>
            <strong>Device Information:</strong> Device type, operating system,
            browser type, IP address, and unique device identifiers
          </li>
          <li>
            <strong>Log Data:</strong> Server logs including IP address, access
            times, pages viewed, and other usage statistics
          </li>
          <li>
            <strong>Cookies and Similar Technologies:</strong> We use cookies,
            web beacons, and similar tracking technologies
          </li>
        </ul>

        <h3>Information from Third Parties</h3>
        <p>
          We may receive information about you from third parties, including:
        </p>
        <ul>
          <li>Other users who mention you or include you in their content</li>
          <li>
            Third-party services you connect to your account (like social media
            platforms)
          </li>
          <li>Analytics providers and advertising partners</li>
          <li>Public databases and data providers</li>
        </ul>
      </LegalSection>

      <LegalSection
        id="how-we-use-information"
        title="How We Use Your Information"
      >
        <p>We use the information we collect to:</p>
        <ul>
          <li>
            <strong>Provide and Improve Services:</strong> Operate, maintain,
            and improve our platform and features
          </li>
          <li>
            <strong>Personalization:</strong> Customize your experience and show
            you relevant content and connections
          </li>
          <li>
            <strong>Communication:</strong> Send you updates, notifications, and
            respond to your inquiries
          </li>
          <li>
            <strong>Safety and Security:</strong> Protect against fraud, abuse,
            and other harmful activities
          </li>
          <li>
            <strong>Analytics:</strong> Understand how our Services are used and
            improve user experience
          </li>
          <li>
            <strong>Legal Compliance:</strong> Comply with legal obligations and
            enforce our terms of service
          </li>
          <li>
            <strong>Marketing:</strong> With your consent, send you promotional
            content about new features and services
          </li>
        </ul>

        <h3>Legal Basis for Processing (GDPR)</h3>
        <p>
          For users in the European Union, we process your personal data based
          on:
        </p>
        <ul>
          <li>
            <strong>Consent:</strong> When you provide explicit consent for
            specific processing activities
          </li>
          <li>
            <strong>Contract:</strong> To fulfill our contractual obligations in
            providing the Services
          </li>
          <li>
            <strong>Legitimate Interest:</strong> For our legitimate business
            interests, such as improving our Services
          </li>
          <li>
            <strong>Legal Obligation:</strong> To comply with applicable laws
            and regulations
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        id="emergency-location-services"
        title="Emergency Services and Location Data"
      >
        <h3>Emergency Location Services</h3>
        <p>
          Locuno provides location-based features that may be used in emergency
          situations. When you enable location services and use
          emergency-related features, we collect and process location data as
          follows:
        </p>

        <h3>Location Data Collection for Emergency Services</h3>
        <p>
          <strong>Types of Location Data:</strong>
        </p>
        <ul>
          <li>
            <strong>Real-time Location:</strong> Your current GPS coordinates
            when you activate emergency features
          </li>
          <li>
            <strong>Location History:</strong> Recent location data to help
            emergency services understand your movement patterns
          </li>
          <li>
            <strong>Device Location:</strong> Location information from your
            device's GPS, Wi-Fi, and cellular networks
          </li>
          <li>
            <strong>Emergency Context:</strong> Additional location context such
            as nearby landmarks or addresses
          </li>
        </ul>

        <h3>Emergency Service Provider Integration</h3>
        <p>
          When you use emergency features, your location data may be shared
          with:
        </p>
        <ul>
          <li>
            <strong>Certified Emergency Service Providers:</strong> Third-party
            emergency service providers who are trained to receive and process
            location-based emergency requests
          </li>
          <li>
            <strong>Emergency Contacts:</strong> Designated emergency contacts
            you have specified in your account settings
          </li>
          <li>
            <strong>Community Safety Networks:</strong> Nearby community members
            who may assist in emergency situations
          </li>
          <li>
            <strong>Public Safety Agencies:</strong> Local emergency services
            (911, 112, etc.) when you explicitly request emergency assistance
          </li>
        </ul>

        <h3>Emergency Service Provider Capabilities</h3>
        <p>
          Our emergency service providers are equipped with systems that can:
        </p>
        <ul>
          <li>
            <strong>Receive Location Data:</strong> Accept and process location
            information from emergency requests
          </li>
          <li>
            <strong>Identify Locations:</strong> Convert GPS coordinates into
            recognizable addresses and landmarks
          </li>
          <li>
            <strong>Process Emergency Requests:</strong> Handle location-based
            emergency notifications according to industry standards
          </li>
          <li>
            <strong>Coordinate Response:</strong> Work with local emergency
            services to facilitate appropriate response
          </li>
        </ul>

        <h3>Data Processing for Emergency Services</h3>
        <p>We process location data for emergency services based on:</p>
        <ul>
          <li>
            <strong>Explicit Consent:</strong> When you activate emergency
            features and consent to location sharing
          </li>
          <li>
            <strong>Legitimate Interest:</strong> To protect your safety and the
            safety of others in emergency situations
          </li>
          <li>
            <strong>Vital Interests:</strong> To protect your life or physical
            safety in emergency situations
          </li>
          <li>
            <strong>Legal Obligation:</strong> When required by law or emergency
            service regulations
          </li>
        </ul>

        <h3>Location Data Retention for Emergency Services</h3>
        <p>Location data used for emergency services is retained:</p>
        <ul>
          <li>
            <strong>Emergency Situations:</strong> For the duration of the
            emergency and immediate aftermath
          </li>
          <li>
            <strong>Service Improvement:</strong> Anonymized data may be
            retained for improving emergency response capabilities
          </li>
          <li>
            <strong>Legal Requirements:</strong> As required by law or emergency
            service regulations
          </li>
          <li>
            <strong>User Request:</strong> Until you request deletion, subject
            to legal retention requirements
          </li>
        </ul>

        <h3>Emergency Service Limitations</h3>
        <p>
          <strong>Important Limitations:</strong>
        </p>
        <ul>
          <li>
            <strong>No Guarantee:</strong> We cannot guarantee that emergency
            services will receive or respond to location data
          </li>
          <li>
            <strong>Technical Dependencies:</strong> Location services depend on
            device capabilities, network connectivity, and GPS signal
          </li>
          <li>
            <strong>User Responsibility:</strong> You remain responsible for
            contacting official emergency services (911, 112, etc.) for
            life-threatening situations
          </li>
          <li>
            <strong>Service Availability:</strong> Emergency features may not be
            available in all locations or circumstances
          </li>
        </ul>

        <h3>Your Rights Regarding Emergency Location Data</h3>
        <p>You have the right to:</p>
        <ul>
          <li>
            <strong>Control Location Sharing:</strong> Enable or disable
            location services for emergency features
          </li>
          <li>
            <strong>Designate Emergency Contacts:</strong> Choose who receives
            your location during emergencies
          </li>
          <li>
            <strong>Access Location Data:</strong> Request copies of location
            data used for emergency services
          </li>
          <li>
            <strong>Delete Location Data:</strong> Request deletion of location
            data, subject to legal requirements
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Revoke consent for emergency
            location sharing at any time
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        id="information-sharing"
        title="Information Sharing and Disclosure"
      >
        <p>We may share your information in the following circumstances:</p>

        <h3>Public Information</h3>
        <p>
          Information you choose to make public (such as your profile, posts,
          and comments) may be visible to other users and the public. Please be
          mindful of what you choose to share publicly.
        </p>

        <h3>With Other Users</h3>
        <p>
          We share information with other users as part of the social networking
          functionality, including your profile information, posts, and
          interactions.
        </p>

        <h3>Service Providers</h3>
        <p>
          We may share information with third-party service providers who
          perform services on our behalf, such as hosting, analytics, customer
          support, and payment processing.
        </p>

        <h3>Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, or sale of assets, your
          information may be transferred as part of that transaction.
        </p>

        <h3>Legal Requirements</h3>
        <p>
          We may disclose information if required by law or if we believe in
          good faith that such disclosure is necessary to:
        </p>
        <ul>
          <li>Comply with legal process or government requests</li>
          <li>Enforce our terms of service or other agreements</li>
          <li>
            Protect the rights, property, or safety of Locuno, our users, or
            others
          </li>
          <li>Prevent fraud or other illegal activities</li>
        </ul>
      </LegalSection>

      <LegalSection id="data-security" title="Data Security">
        <p>
          We implement appropriate technical and organizational measures to
          protect your personal information against unauthorized access,
          alteration, disclosure, or destruction. These measures include:
        </p>
        <ul>
          <li>Encryption of data in transit and at rest</li>
          <li>Regular security assessments and updates</li>
          <li>Access controls and authentication procedures</li>
          <li>Employee training on data protection</li>
          <li>Incident response procedures</li>
        </ul>
        <p>
          However, no method of transmission over the internet or electronic
          storage is 100% secure. While we strive to protect your information,
          we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection id="your-rights" title="Your Rights and Choices">
        <h3>Account Settings</h3>
        <p>
          You can update your account information and privacy settings through
          your account dashboard.
        </p>

        <h3>Data Access and Portability</h3>
        <p>
          You have the right to access and download your personal data. You can
          request a copy of your data through your account settings.
        </p>

        <h3>Data Correction and Deletion</h3>
        <p>
          You can correct inaccurate information or request deletion of your
          account and personal data.
        </p>

        <h3>Communication Preferences</h3>
        <p>
          You can opt out of promotional communications by following the
          unsubscribe instructions or updating your preferences.
        </p>

        <h3>GDPR Rights (EU Users)</h3>
        <p>
          If you are in the European Union, you have additional rights
          including:
        </p>
        <ul>
          <li>Right to rectification of inaccurate data</li>
          <li>Right to erasure ("right to be forgotten")</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to processing</li>
          <li>Right to withdraw consent</li>
        </ul>

        <h3>California Privacy Rights (CCPA)</h3>
        <p>California residents have the right to:</p>
        <ul>
          <li>Know what personal information is collected</li>
          <li>Delete personal information</li>
          <li>Opt-out of the sale of personal information</li>
          <li>Non-discrimination for exercising privacy rights</li>
        </ul>
      </LegalSection>

      <LegalSection id="cookies" title="Cookies and Tracking Technologies">
        <p>
          We use cookies and similar technologies to enhance your experience,
          analyze usage, and provide personalized content. For detailed
          information about our use of cookies, please see our
          <a href="/pages/cookies" className="text-blue-600 hover:underline">
            Cookie Policy
          </a>
          .
        </p>
        <p>
          You can control cookies through your browser settings, but disabling
          cookies may affect the functionality of our Services.
        </p>
      </LegalSection>

      <LegalSection
        id="international-transfers"
        title="International Data Transfers"
      >
        <p>
          Your information may be transferred to and processed in countries
          other than your country of residence. These countries may have
          different data protection laws than your country.
        </p>
        <p>
          When we transfer personal data from the European Union to other
          countries, we ensure appropriate safeguards are in place, such as
          Standard Contractual Clauses or adequacy decisions.
        </p>
      </LegalSection>

      <LegalSection id="children-privacy" title="Children's Privacy">
        <p>
          Our Services are not intended for children under 13 years of age. We
          do not knowingly collect personal information from children under 13.
          If we become aware that we have collected personal information from a
          child under 13, we will take steps to delete such information.
        </p>
        <p>
          If you are a parent or guardian and believe your child has provided us
          with personal information, please contact us so we can take
          appropriate action.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make
          changes, we will notify you by updating the "Last Updated" date and,
          for material changes, we may provide additional notice through our
          Services or email.
        </p>
        <p>
          Your continued use of our Services after any changes to this Privacy
          Policy constitutes your acceptance of the updated policy.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact Us">
        <p>
          If you have any questions about this Privacy Policy or our privacy
          practices, please contact us:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          <p>
            <strong>Company Name:</strong> CLOUDJET SOLUTIONS COMPANY LIMITED
          </p>
          <p>
            <strong>Address:</strong> 2nd Floor, F Central Building, 16A Le Hong
            Phong, Ward 12, District 10, Ho Chi Minh City, Vietnam
          </p>
          <p>
            <strong>Tax Code:</strong> 0312033924
          </p>
          <p>
            <strong>Phone:</strong> 028 628 00110
          </p>
          <p>
            <strong>Email:</strong> contact@locuno.com
          </p>
          <p>
            <strong>Privacy:</strong> privacy@locuno.com
          </p>
          <p>
            <strong>Data Protection Officer:</strong> dpo@locuno.com
          </p>
        </div>
      </LegalSection>
    </LegalPageLayout>
  );
}
